import mixpanel, { Mixpanel } from 'mixpanel-browser';
import * as R from 'ramda';

import config from '@/config';
import { ComplianceTypes, urls } from '@/constants';
import {
  AnalyticEventProps,
  AssessmentEventAttemptProps,
  BookmarkEventProps,
  CarouselScrollEventProps,
  CarouselSwitchTabEventProps,
  ClickThroughProps,
  ComplianceType,
  CsvVariants,
  Episode,
  EpisodeDetailSwitchTabProps,
  EpisodeEvaluationEventProps,
  EpisodeEventProps,
  EpisodeFeedbackEventProps,
  IterableChannelData,
  LearningPathCardClickedEventProps,
  LearningPathEventProps,
  LearningPathSidebarItemClickedEventProps,
  LearningPathsPageViewedEventProps,
  LearningPathViewAllClickedEventProps,
  LearningPathViewModeToggledEventProps,
  LibraryFilters,
  Location,
  MemberManagementEventProps,
  Order,
  PauseEventProps,
  PlaybackSpeedChangedEventProps,
  ProductFeedbackEventProps,
  QuizAttemptEventProps,
  QuizViewEventProps,
  Referrer,
  Region,
  ResourceViewEventProps,
  SeekEventProps,
  SkipEventProps,
  UserProfile,
  VideoPlayerFullScreenEventProps,
  VolumeEventProps,
} from '@/types';
import {
  ageInHours,
  convertSecondsToHoursMinutes,
  getIsEpisodeEthics,
  isAnalyticsConsoleLogsEnabled,
} from '@/utils';

import { growthbook } from '../features';
import { ParamObject, sessionConfig, validateParamValue } from './config';
import { EVENT_CONSTANTS, EVENTS, GTM_EVENT_ALLOWLIST } from './events';
import { EventName } from './gtag.types';
import {
  gtmEvent,
  resetAllGtmUserProps,
  setGtmUserData,
  setGtmUserID,
  setGtmUserProperties,
  SingleValueTags,
  TagNameLookup,
} from './gtm';

export const IS_DEV = process.env.NODE_ENV === 'development';

let userProperties: Record<
  string,
  boolean | undefined | string | number | Date
> = {};
let userTags = {};
let utmParams: Record<string, string> = {};
let featureFlags: Record<string, string> = {};

interface ProfileProps {
  complianceType?: ComplianceType;
  userProfile?: UserProfile;
  organizationName?: string;
  userTags?: string[];
}

/* User-level Events */

export function initialize() {
  mixpanel.init(config.MIX_PANEL_PROJECT_TOKEN, {
    api_host: urls.actions.mixpanel,
    cookie_domain: '.lumiqlearn.com',
    record_idle_timeout_ms: 60 * 60 * 1000, // 1 hour
    record_block_selector: '',
    stop_utm_persistence: true,
    loaded(mp) {
      sessionConfig.checkSessionId();
      const originalTrack = mp.track;
      mp.track = function (...args: Parameters<Mixpanel['track']>) {
        sessionConfig.checkSessionId();
        mp.register({ last_event_time: Date.now() });
        originalTrack.apply(mp, args);
      };
    },

    // Note: These props are valid but aren't reflected in types yet
    record_min_ms: 5 * 1000, // Ignore sessions shorter than 5 seconds
    record_heatmap_data: true,
  });
}

export const setUtmParametersAnalytics = (params: Record<string, string>) => {
  utmParams = params;
};

export function identify({ userId }: { userId?: string }) {
  const currentDistinctId = mixpanel.get_distinct_id();
  console.log('current distinct id:', currentDistinctId);

  if (userId) {
    console.log('identifying mixpanel with userId', userId);
    mixpanel.identify(userId);
    mixpanel.register({
      user_id: userId,
    });
    console.log('new distinct id:', mixpanel.get_distinct_id());

    setGtmUserID(userId);
  }
}

export function reset() {
  const currentDistinctId = mixpanel.get_distinct_id();
  console.log('current distinct id:', currentDistinctId);
  console.log('resetting mixpanel');
  mixpanel.reset();
  console.log('new distinct id:', mixpanel.get_distinct_id());
  resetAllGtmUserProps();
}

export const parseUserTags = (tags: string[] | undefined) => {
  return (
    tags?.reduce(
      (obj, tag: string) => {
        const [key, value] = tag.split(':');
        const fieldName = `$user_tag - ${key}`;
        return { ...obj, [fieldName]: [...(obj[fieldName] || []), value] };
      },
      {} as Record<string, string[]>,
    ) || {}
  );
};

export function parseUserTagsForGTM(tags: string[] | undefined) {
  return tags?.reduce(
    (acc, t) => {
      const [key, value] = t.split(':');
      // make key readable
      const keyName = TagNameLookup[key as keyof typeof TagNameLookup] ?? key;

      if (
        SingleValueTags.includes(keyName as (typeof SingleValueTags)[number])
      ) {
        return {
          ...acc,
          [keyName]: value,
        };
      }
      return {
        ...acc,
        [keyName]: [...(acc[keyName] ?? []), value],
      };
    },
    {} as Record<
      (typeof TagNameLookup)[keyof typeof TagNameLookup],
      string[] | string
    >,
  );
}

/* User Profile Events & Subscription Center Events */
export function updateUserProfile({
  complianceType,
  userProfile,
  organizationName,
  userTags: tags,
}: ProfileProps) {
  userProperties = {
    complianceType,
    nasba: complianceType === ComplianceTypes.CPE,
    $email: userProfile?.email,
    ...(organizationName ? { organizationName } : {}),
    ...(userProfile
      ? {
          $name: `${userProfile.firstName} ${userProfile.lastName}`,
          jobTitle: userProfile.jobTitle || '',
          signupDate: userProfile.userCreatedAt,
          onboardingDate: userProfile.onboardingCompletedAt,
        }
      : {}),
  };

  userTags = parseUserTags(tags);

  mixpanel.people.set(userProperties);
  mixpanel.people.set(userTags);

  if (userProfile) {
    setGtmUserData(
      userProfile.email,
      userProfile.firstName,
      userProfile.lastName,
    );
  }

  const normalizedTags = parseUserTagsForGTM(tags);

  setGtmUserProperties({
    ...(normalizedTags ? { userTags: normalizedTags } : {}),
    ...(complianceType ? { complianceType } : {}),
    ...(organizationName ? { organizationName } : {}),
    ...(userProfile
      ? {
          signupDate: userProfile.userCreatedAt,
          onboardingDate: userProfile.onboardingCompletedAt,
        }
      : {}),
  });
}

export function updateEmailPreferences() {
  logEvent(EVENTS.EMAIL_PREFERENCES_EVENTS.UPDATE_SUBSCRIPTIONS);
}

export function toggleEmailPreference({
  channel,
}: {
  channel: IterableChannelData;
}) {
  logEvent(EVENTS.EMAIL_PREFERENCES_EVENTS.TOGGLE_SUBSCRIPTION, {
    channelName: validateParamValue(channel.name),
    channelId: channel.id,
    project: channel.project,
    isSubscribed: !channel.isSubscribed,
  });
}

export function keepEmailSubscription({
  channelName,
  groupName,
}: {
  channelName?: string;
  groupName: string;
}) {
  logEvent(EVENTS.EMAIL_PREFERENCES_EVENTS.KEEP_SUBSCRIPTION, {
    channelName: validateParamValue(channelName),
    groupName: validateParamValue(groupName),
  });
}

export function confirmEmailUnsubscribe({
  channelName,
  groupName,
}: {
  channelName?: string;
  groupName: string;
}) {
  logEvent(EVENTS.EMAIL_PREFERENCES_EVENTS.CONFIRM_UNSUBSCRIBE, {
    channelName: validateParamValue(channelName),
    groupName: validateParamValue(groupName),
  });
}

export const updateFeatureFlags = (flags: object) => {
  featureFlags = { ...featureFlags, ...flags };
};

/* Navigation Events */

export const pageLoad = (args: {
  queryParams?: object;
  campaignId?: string;
  isFromNewsletter?: string;
  pathname?: string;
}) => {
  const {
    queryParams = {},
    campaignId = '',
    isFromNewsletter = false,
    pathname = '',
  } = args;
  logEvent(EVENTS.PAGE_EVENTS.LOAD, {
    queryParams,
    campaignId: validateParamValue(campaignId),
    isFromNewsletter,
    pathname,
  });
};

export const pageView = (location: Location) => {
  logEvent(EVENTS.PAGE_EVENTS.VIEW, {
    location: validateParamValue(location || ''),
  });
};

export const pageViewEpisode = ({
  episode,
  referrer,
}: {
  episode: Episode;
  referrer: Referrer;
}) => {
  logEvent(EVENTS.PAGE_EVENTS.VIEW_EPISODE, {
    episodeId: episode.episodeId,
    episodeName: episode.name,
    episodeFormat: episode.isVideo ? 'video' : 'audio',
    episodeDuration: episode.duration,
    formattedEpisodeDuration: convertSecondsToHoursMinutes(episode.duration),
    episodeChapters: episode.chapters?.length,
    episodeTypes: [
      ...(episode.isRecordedLive ? ['live'] : []),
      ...(episode.isPremium ? ['premium'] : []),
      ...(getIsEpisodeEthics({
        episode,
        compliance: userProperties.nasba
          ? ComplianceTypes.CPE
          : ComplianceTypes.CPD,
      })
        ? ['ethics']
        : []),
    ],
    location: validateParamValue(referrer || ''),
  });
};

export const pageViewPricing = ({ location }: { location: Location }) => {
  logEvent(EVENTS.PAGE_EVENTS.VIEW_PRICING, {
    location: validateParamValue(location || ''),
  });
};

export const clickThrough = ({
  target,
  referrer,
  section,
  resourceId,
}: ClickThroughProps) => {
  logEvent(EVENTS.CLICK_THROUGH, {
    target,
    location: validateParamValue(referrer || ''),
    section,
    resourceId,
  });
};

export function carouselScroll({
  direction,
  location,
  referrer,
}: CarouselScrollEventProps) {
  logEvent(EVENTS.CAROUSEL_EVENTS.SCROLL, {
    direction,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
  });
}

export function carouselSwitchTab({
  tabName,
  location,
  referrer,
}: CarouselSwitchTabEventProps) {
  logEvent(EVENTS.CAROUSEL_EVENTS.SWITCH_TAB, {
    tabName,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || location || ''),
  });
}

export function switchRegion({
  oldRegion,
  newRegion,
}: {
  oldRegion: Region;
  newRegion: Region;
}) {
  logEvent(EVENTS.CHANGE_REGION, {
    oldRegion,
    newRegion,
  });
}

/* Chapter-level Events */

export function chapterPlay({
  chapterId,
  episodeId,
  location,
  referrer,
}: AnalyticEventProps) {
  logEvent(EVENTS.CHAPTER_EVENTS.PLAY, {
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
  });
}

export function chapterPause({
  chapterId,
  episodeId,
  location,
  referrer,
  pausing,
}: PauseEventProps) {
  logEvent(
    pausing ? EVENTS.CHAPTER_EVENTS.PAUSE : EVENTS.CHAPTER_EVENTS.RESUME,
    {
      chapterId,
      episodeId,
      location: validateParamValue(location || ''),
      referrer: validateParamValue(referrer || ''),
    },
  );
}

export function chapterSeek({
  alreadyListened,
  chapterId,
  episodeId,
  seekStart,
  seekLength,
  type,
}: SeekEventProps) {
  logEvent(EVENTS.CHAPTER_EVENTS.SEEK, {
    alreadyListened,
    chapterId,
    episodeId,
    seekStart,
    seekLength,
    type: validateParamValue(type || ''),
  });
}

export function chapterStart({
  chapterId,
  episodeId,
  location,
  referrer,
}: AnalyticEventProps) {
  logEvent(EVENTS.CHAPTER_EVENTS.START, {
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
  });
}

export function chapterComplete({ chapterId, episodeId }: AnalyticEventProps) {
  logEvent(EVENTS.CHAPTER_EVENTS.COMPLETE, {
    chapterId,
    episodeId,
  });
}

/* Track Player-level Events */

export function playbackSpeedChanged({
  speed,
  chapterId,
  episodeId,
  location,
}: PlaybackSpeedChangedEventProps) {
  logEvent(EVENTS.CHAPTER_EVENTS.PLAYBACK_SPEED_CHANGED, {
    speed,
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
  });

  logEvent(EVENTS.TRACK_PLAYER_EVENTS.CHANGE_SPEED, {
    speed,
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
  });
}

export function trackPlayerPlayPause({
  chapterId,
  episodeId,
  location,
  referrer,
  pausing,
}: PauseEventProps) {
  logEvent(
    pausing
      ? EVENTS.TRACK_PLAYER_EVENTS.PAUSE
      : EVENTS.TRACK_PLAYER_EVENTS.PLAY_RESUME,
    {
      chapterId,
      episodeId,
      location: validateParamValue(location || ''),
      referrer: validateParamValue(referrer || ''),
    },
  );
}

export function trackPlayerSeek({
  alreadyListened,
  chapterId,
  episodeId,
  seekStart,
  seekLength,
  type,
  location,
}: SeekEventProps) {
  logEvent(EVENTS.TRACK_PLAYER_EVENTS.CHANGE_POSITION, {
    alreadyListened,
    chapterId,
    episodeId,
    seekStart,
    seekLength,
    type: validateParamValue(type || ''),
    location: validateParamValue(location || ''),
  });
}

export function trackPlayerVolume({
  chapterId,
  episodeId,
  val,
  location,
}: VolumeEventProps) {
  logEvent(EVENTS.TRACK_PLAYER_EVENTS.ADJUST_VOLUME, {
    chapterId,
    episodeId,
    volume: val,
    location: validateParamValue(location || ''),
  });

  if (val <= 0) {
    logEvent(EVENTS.TRACK_PLAYER_EVENTS.MUTE, {
      chapterId,
      episodeId,
      volume: val,
      location: validateParamValue(location || ''),
    });
  }
}

export function trackPlayerSkip({
  chapterId,
  episodeId,
  direction,
}: SkipEventProps) {
  logEvent(EVENTS.TRACK_PLAYER_EVENTS.SKIP_CHAPTER, {
    chapterId,
    episodeId,
    skipDirection: direction,
  });
}

export function trackPlayerRewind({
  chapterId,
  episodeId,
  location,
}: AnalyticEventProps) {
  logEvent(EVENTS.TRACK_PLAYER_EVENTS.REWIND, {
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
  });
}

export function trackPlayerViewEpisode({
  chapterId,
  episodeId,
  location,
  referrer,
}: AnalyticEventProps) {
  logEvent(EVENTS.TRACK_PLAYER_EVENTS.VIEW_EPISODE_DETAILS, {
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
  });
}

export function trackPlayerFeedback({
  chapterId,
  episodeId,
}: AnalyticEventProps) {
  logEvent(EVENTS.TRACK_PLAYER_EVENTS.EPISODE_FEEDBACK, {
    chapterId,
    episodeId,
  });
}

export function videoPlayerFullScreen({
  chapterId,
  episodeId,
  isFullScreen,
}: VideoPlayerFullScreenEventProps) {
  logEvent(EVENTS.VIDEO_PLAYER_EVENTS.FULLSCREEN, {
    chapterId,
    episodeId,
    isFullScreen,
  });
}

/* Admin Events - Member Management & Reporting Events */

export function removeMember({
  organizationMemberIds,
  location,
  referrer,
}: MemberManagementEventProps) {
  logEvent(EVENTS.MEMBER_MANAGEMENT_EVENTS.REMOVE_MEMBER, {
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
    organizationMemberIds,
  });
}

export function resendInvite({
  organizationMemberIds,
  location,
  referrer,
}: MemberManagementEventProps) {
  logEvent(EVENTS.MEMBER_MANAGEMENT_EVENTS.RESEND_INVITE, {
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
    organizationMemberIds,
  });
}

export function downloadReportingCsv({
  reportType,
}: {
  reportType: (typeof CsvVariants)[keyof typeof CsvVariants];
}) {
  logEvent(EVENTS.REPORTING_EVENTS.DOWNLOAD_CSV, {
    reportType,
  });
}

export function seeMembersClicked() {
  logEvent(EVENTS.REPORTING_EVENTS.SEE_MEMBERS);
}

// UNUSED - Dev only?
export function debug(eventName: string, data: {}) {
  logEvent(EVENTS.DEBUG, { eventName, ...data });
}

export function updateContentPreferenceRegions({
  previousValue,
  newValue,
  location,
}: {
  previousValue: string[];
  newValue: string[];
  location?: Location;
}) {
  logEvent(EVENTS.CONTENT_PREFERENCES_EVENTS.UPDATE_REGIONS, {
    previous_value: previousValue,
    new_value: newValue,
    location: validateParamValue(location || ''),
  });
}

/* Episode-level Events */

export function episodeStart({
  hasVisualAid,
  chapterId,
  episodeId,
  location,
  referrer,
  hasTranscript,
}: AnalyticEventProps & { hasTranscript: boolean; hasVisualAid: boolean }) {
  logEvent(EVENTS.EPISODE_EVENTS.START, {
    chapterId,
    episodeId,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
    hasTranscript,
    hasVisualAid,
  });
}

export function episodeListened({ episodeId }: EpisodeEventProps) {
  logEvent(EVENTS.EPISODE_EVENTS.LISTENED, {
    episodeId,
  });
}

export function episodeComplete({ episodeId }: EpisodeEventProps) {
  logEvent(EVENTS.EPISODE_EVENTS.COMPLETE, {
    episodeId,
  });
}

export function episodeImpression({
  episodeId,
  location,
  referrer,
}: EpisodeEventProps) {
  logEvent(EVENTS.EPISODE_EVENTS.IMPRESSION, {
    episodeId,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
  });
}

export function learningPathImpression({
  learningPathId,
  location,
}: LearningPathEventProps) {
  logEvent(EVENTS.LEARNING_PATH_EVENTS.IMPRESSION, {
    learningPathId,
    location: validateParamValue(location || ''),
  });
}

export function learningPathCardClicked({
  learningPathId,
  location,
  referrer,
  episodesCompleted,
  isCompleted,
  topic,
  isRecommended,
}: LearningPathCardClickedEventProps) {
  logEvent(EVENTS.LEARNING_PATH_EVENTS.CARD_CLICKED, {
    learningPathId,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
    episodesCompleted,
    isCompleted,
    topic,
    isRecommended,
  });
}

export function learningPathSidebarItemClicked({
  path,
  label,
}: LearningPathSidebarItemClickedEventProps) {
  logEvent(EVENTS.LEARNING_PATH_EVENTS.SIDEBAR_ITEM_CLICKED, {
    path,
    label,
  });
}

export function learningPathsPageViewed({
  inProgressPathsCount,
  recommendedPathsCount,
  viewMode,
  pathname,
}: LearningPathsPageViewedEventProps) {
  logEvent(EVENTS.LEARNING_PATH_EVENTS.PAGE_VIEWED, {
    inProgressPathsCount,
    recommendedPathsCount,
    viewMode,
    pathname,
  });
}

export function learningPathViewModeToggled({
  viewMode,
}: LearningPathViewModeToggledEventProps) {
  logEvent(EVENTS.LEARNING_PATH_EVENTS.VIEW_MODE_TOGGLED, {
    viewMode,
  });
}

export function learningPathViewAllClicked({
  section,
}: LearningPathViewAllClickedEventProps) {
  logEvent(EVENTS.LEARNING_PATH_EVENTS.VIEW_ALL_CLICKED, {
    section,
  });
}

export function episodeRedeem({ episodeId }: EpisodeEventProps) {
  logEvent(EVENTS.EPISODE_EVENTS.REDEEM, {
    episodeId,
  });
}

export function episodeShareCopy({ episodeId, location }: EpisodeEventProps) {
  logEvent(EVENTS.EPISODE_SHARE_EVENTS.LINK_COPY, {
    episodeId,
    location: validateParamValue(location || ''),
  });
}

export function bookmark({
  episodeId,
  value,
  location,
  referrer,
}: BookmarkEventProps) {
  logEvent(EVENTS.EPISODE_EVENTS.BOOKMARK, {
    episodeId,
    value,
    location: validateParamValue(location || ''),
    referrer: validateParamValue(referrer || ''),
  });

  if (location?.component === EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER) {
    logEvent(EVENTS.TRACK_PLAYER_EVENTS.BOOKMARK, {
      episodeId,
      value,
      location: validateParamValue(location || ''),
      referrer: validateParamValue(referrer || ''),
    });
  }
}

export function episodeFeedback({
  episodeRating,
  episodeId,
  chapterId,
  chapterProgress,
  episodePlayedDuration,
  textFeedback,
  ratingReasons,
}: EpisodeFeedbackEventProps) {
  logEvent(EVENTS.EPISODE_FEEDBACK_EVENTS.FEEDBACK, {
    episodeRating,
    chapterId,
    chapterProgress,
    episodePlayedDuration,
    episodeId,
    ratingReasons: validateParamValue(ratingReasons || ''),
    textFeedback: validateParamValue(textFeedback || ''),
  });
}

export function productFeedback({
  productFeedbackForm,
  isDismissed = false,
  referrer,
}: ProductFeedbackEventProps) {
  logEvent(EVENTS.PRODUCT_FEEDBACK_EVENTS.FEEDBACK, {
    ...R.map(validateParamValue, productFeedbackForm),
    isDismissed,
    referrer: validateParamValue(referrer || ''),
  });
}

export function episodeEvaluation({
  episodeId,
  answers,
}: EpisodeEvaluationEventProps) {
  logEvent(EVENTS.EPISODE_FEEDBACK_EVENTS.EVALUATION, {
    episodeId,
    answers,
  });
}

export function episodeDetailSwitchTab({
  episodeId,
  tabName,
}: EpisodeDetailSwitchTabProps) {
  logEvent(EVENTS.EPISODE_DETAIL_EVENTS.SWITCH_TAB, {
    episodeId,
    tabName,
  });
}

export function visualAidView({ episodeId }: EpisodeEventProps) {
  logEvent(EVENTS.VISUAL_AID_VIEW, {
    episodeId,
  });
}

export function resourceView({
  resourceName,
  episodeId,
}: ResourceViewEventProps) {
  if (episodeId) {
    logEvent(EVENTS.RESOURCE_VIEW, {
      resourceName,
      episodeId,
    });
  } else {
    logEvent(EVENTS.RESOURCE_VIEW, { resourceName });
  }
}

export function quizAttempt({
  chapterId,
  quizId,
  answer,
  pass,
}: QuizAttemptEventProps) {
  logEvent(EVENTS.QUIZ_EVENTS.ATTEMPT, {
    chapterId,
    quizId,
    answer,
    pass: !!pass,
  });
  logEvent(pass ? EVENTS.QUIZ_EVENTS.PASS : EVENTS.QUIZ_EVENTS.FAIL, {
    chapterId,
    quizId,
    answer,
    pass: !!pass,
  });
}

export function quizView({
  chapterId,
  quizId,
  question,
  isAvailable,
  isAnswered,
}: QuizViewEventProps) {
  logEvent(EVENTS.QUIZ_EVENTS.VIEW, {
    chapterId,
    quizId,
    question: validateParamValue(question || ''),
    isAvailable,
    isAnswered,
  });
}

export function assessmentAttempt({
  episodeId,
  assessmentId,
  answer,
  score,
  pass,
}: AssessmentEventAttemptProps) {
  const assessmentDetails = {
    episodeId,
    assessmentId,
    answer,
    score,
    pass,
  };

  logEvent(EVENTS.ASSESSMENT_EVENTS.ATTEMPT, assessmentDetails);
  logEvent(
    pass ? EVENTS.ASSESSMENT_EVENTS.PASS : EVENTS.ASSESSMENT_EVENTS.FAIL,
    assessmentDetails,
  );
}

export function purchaseConversion({ order }: { order: Order }) {
  logEvent(EVENTS.GA4_KEY_EVENTS.PURCHASE, {
    value: order.offer.price, // Should not include tax or discount
    currency: order.offer.currency,
    transaction_id: order.orderId,
    items: [
      {
        item_id: order.offer.sku,
        item_name: order.offer.productName,
        discount: order.offer.discount,
        price: order.offer.price,
        quantity: 1,
      },
    ],
  });
}

/* Library & Search Events */

export function searchSetFilter({
  searchTerm,
  referrer,
}: {
  searchTerm: string;
  referrer?: Referrer;
}) {
  logEvent(EVENTS.SEARCH_EVENTS.SET_FILTER, {
    searchTerm,
    location: validateParamValue(referrer || ''),
  });
}

export function searchSelectQuerySuggestion({
  searchTerm,
  selectedQuerySuggestion,
  selectedQueryPosition,
}: {
  searchTerm: string;
  selectedQuerySuggestion: string;
  selectedQueryPosition: number;
}) {
  logEvent(EVENTS.SEARCH_EVENTS.SELECT_QUERY_SUGGESTION, {
    searchTerm,
    selectedQuerySuggestion,
    selectedQueryPosition,
  });
}

export function libraryFilterChange({
  newFilters,
  prevFilters,
}: {
  newFilters: LibraryFilters | null;
  prevFilters: LibraryFilters | null;
}) {
  if (!newFilters || !prevFilters) return;
  if (newFilters.sortBy && !R.equals(newFilters.sortBy, prevFilters.sortBy)) {
    logEvent(EVENTS.LIBRARY_EVENTS.SORT, {
      previous_value: prevFilters.sortBy,
      new_value: newFilters.sortBy,
    });
  }
  if (
    newFilters.mediaType &&
    !R.equals(newFilters.mediaType, prevFilters.mediaType)
  ) {
    logEvent(EVENTS.LIBRARY_EVENTS.MEDIA_TYPE, {
      previous_value: prevFilters.mediaType,
      new_value: newFilters.mediaType,
    });
  }
}

export function filterLibraryClear({
  categoryName,
}: {
  categoryName: string | undefined;
}) {
  logEvent(
    categoryName === EVENT_CONSTANTS.CLEAR_ALL
      ? EVENTS.FILTER_LIBRARY_EVENTS.FILTER_CLEAR
      : EVENTS.FILTER_LIBRARY_EVENTS.FILTER_REMOVE,
    { categoryName: validateParamValue(categoryName || '') },
  );
}

export function closePostQuizRecommendations() {
  logEvent(EVENTS.POST_QUIZ_RECOMMENDATIONS_EVENTS.CLOSE, {});
}

/* Local Functions */

export function showModal(name: string, location?: Location) {
  logEvent(EVENTS.MODAL_EVENTS.OPEN, {
    modalName: name,
    location: validateParamValue(location || ''),
  });
}

export function closeModal(name: string, location?: Location) {
  logEvent(EVENTS.MODAL_EVENTS.CLOSE, {
    modalName: name,
    location: validateParamValue(location || ''),
  });
}

export function buttonClick({ name }: { name: string }) {
  logEvent(EVENTS.BUTTON_CLICK, { buttonName: name });
}

// Avoid sending excessive events in development to reduce mixpanel usage rates
const DEV_BLACKLIST = [
  EVENTS.EPISODE_EVENTS.IMPRESSION,
  EVENTS.LEARNING_PATH_EVENTS.IMPRESSION,
];

export function logEvent(
  eventName: string,
  data: Record<string, ParamObject> = {},
) {
  const blacklist = growthbook
    .getFeatureValue('event-blacklist', '')
    .split('|');

  if (blacklist.includes(eventName)) {
    console.info('Skipping blacklisted event:', eventName);
    return;
  }

  const { referrer, ...otherData } = data;
  const payload: typeof userProperties = {
    ...(otherData || {}),
    ...userProperties,
    ...userTags,
    ...utmParams,
    ...Object.entries(featureFlags).reduce(
      (obj, [key, value]) => ({
        ...obj,
        [`Feature Flag - ${key}`]: value,
      }),
      {},
    ),
  };

  if (payload.onboardingDate) {
    payload.onboardingAge = ageInHours(userProperties.onboardingDate as string);
  }

  if (payload.signupDate) {
    payload.signupAge = ageInHours(userProperties.signupDate as string);
  }

  if (isAnalyticsConsoleLogsEnabled()) {
    console.info(eventName, payload);
  }

  if (IS_DEV && DEV_BLACKLIST.includes(eventName)) {
    return;
  }

  mixpanel.track(eventName, payload);

  if (GTM_EVENT_ALLOWLIST.includes(eventName)) {
    const gtmPayload = otherData;
    gtmEvent(eventName as EventName, gtmPayload);
  }
}
